import React, { useState, useCallback, useMemo } from "react";
import api from '../../lib/axios';
import { toast } from "sonner";
import {
  FileSpreadsheet,
  Upload,
  Download,
  X,
  Check,
  AlertTriangle,
  Plus,
  Edit3,
  Trash2,
  Calculator,
  DollarSign,
  Percent,
  Package,
  Truck,
  Search,
  ChevronsUpDown,
  FileText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import ImageThumbnail from "@/components/common/ImageThumbnail";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(value);
};

const formatNumber = (value, decimals = 2) => {
  return new Intl.NumberFormat('es-PE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value);
};

/**
 * WindoorCraft Upload Section
 * Handles parsing multiple WindoorCraft export Excel files
 */
export const WindoorCraftUploadSection = ({ onParsed, isLoading, setIsLoading }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [processedFiles, setProcessedFiles] = useState([]);

  const processMultipleFiles = useCallback(async (files) => {
    setIsLoading(true);
    setUploadQueue(files.map(f => ({ name: f.name, status: 'pending' })));
    setProcessedFiles([]);
    
    let allItems = [];
    let allImages = [];
    let projectName = null;
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Update queue status
      setUploadQueue(prev => prev.map((item, idx) => 
        idx === i ? { ...item, status: 'processing' } : item
      ));
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await api.post(`${API}/sales/parse-windoorcraft`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (response.data.items && response.data.items.length > 0) {
          allItems = [...allItems, ...response.data.items];
          allImages = [...allImages, ...(response.data.all_images || [])];
          
          if (!projectName && response.data.project_name) {
            projectName = response.data.project_name;
          }
          
          setUploadQueue(prev => prev.map((item, idx) => 
            idx === i ? { ...item, status: 'success', items: response.data.items.length } : item
          ));
          
          setProcessedFiles(prev => [...prev, {
            filename: file.name,
            items: response.data.items.length,
            status: 'success'
          }]);
        } else {
          setUploadQueue(prev => prev.map((item, idx) => 
            idx === i ? { ...item, status: 'warning', message: 'Sin elementos' } : item
          ));
        }
      } catch (error) {
        setUploadQueue(prev => prev.map((item, idx) => 
          idx === i ? { ...item, status: 'error', message: error.response?.data?.detail || 'Error' } : item
        ));
      }
    }
    
    setIsLoading(false);
    
    if (allItems.length > 0) {
      toast.success(`Se encontraron ${allItems.length} elementos en ${files.length} archivo(s)`);
      onParsed({
        filename: files.length === 1 ? files[0].name : `${files.length} archivos`,
        items: allItems,
        all_images: allImages,
        project_name: projectName,
        total_items: allItems.length
      });
    } else {
      toast.warning("No se encontraron elementos en los archivos");
    }
  }, [onParsed, setIsLoading]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(
      f => f.name.endsWith('.xlsx') || f.name.endsWith('.xls')
    );
    if (files.length > 0) {
      await processMultipleFiles(files);
    }
  }, [processMultipleFiles]);

  const handleFileSelect = useCallback(async (e) => {
    const files = Array.from(e.target.files || []).filter(
      f => f.name.endsWith('.xlsx') || f.name.endsWith('.xls')
    );
    if (files.length > 0) {
      await processMultipleFiles(files);
    }
    // Reset input
    e.target.value = '';
  }, [processMultipleFiles]);

  const clearQueue = () => {
    setUploadQueue([]);
    setProcessedFiles([]);
  };

  return (
    <div className="space-y-4">
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          isDragging ? 'border-blue-500 bg-blue-50' : 'border-slate-300 hover:border-slate-400'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        data-testid="windoorcraft-upload-zone"
      >
        <FileSpreadsheet className="w-10 h-10 mx-auto text-slate-400 mb-3" />
        <p className="text-slate-600 mb-1">
          Arrastra archivos WindoorCraft Excel aquí
        </p>
        <p className="text-sm text-slate-500 mb-3">
          Puedes subir <strong>múltiples archivos</strong> a la vez
        </p>
        <input
          type="file"
          accept=".xlsx,.xls"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          id="windoorcraft-file-input"
          data-testid="windoorcraft-file-input"
        />
        <Button 
          variant="outline" 
          className="cursor-pointer" 
          disabled={isLoading}
          onClick={() => document.getElementById('windoorcraft-file-input')?.click()}
        >
          <Upload className="w-4 h-4 mr-2" />
          {isLoading ? "Procesando..." : "Seleccionar archivos"}
        </Button>
      </div>
      
      {/* Upload Queue Progress */}
      {uploadQueue.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">Procesando archivos</CardTitle>
              <Button variant="ghost" size="sm" onClick={clearQueue}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-2">
              {uploadQueue.map((file) => (
                <div 
                  key={`upload-${file.name}-${file.status}`} 
                  className="flex items-center justify-between text-sm py-1 border-b last:border-0"
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <FileSpreadsheet className="w-4 h-4 flex-shrink-0 text-slate-400" />
                    <span className="truncate">{file.name}</span>
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    {file.status === 'pending' && (
                      <Badge variant="secondary">Pendiente</Badge>
                    )}
                    {file.status === 'processing' && (
                      <Badge variant="outline" className="animate-pulse">
                        Procesando...
                      </Badge>
                    )}
                    {file.status === 'success' && (
                      <Badge className="bg-emerald-100 text-emerald-700">
                        <Check className="w-3 h-3 mr-1" />
                        {file.items} items
                      </Badge>
                    )}
                    {file.status === 'warning' && (
                      <Badge className="bg-amber-100 text-amber-700">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        {file.message}
                      </Badge>
                    )}
                    {file.status === 'error' && (
                      <Badge variant="destructive">
                        <X className="w-3 h-3 mr-1" />
                        {file.message}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

/**
 * WindoorCraft Parsed Items Modal
 * Shows parsed items with matching info and allows editing
 */
export const WindoorCraftParsedItemsModal = ({
  isOpen,
  onClose,
  parsedData,
  onSave,
  isSaving,
  existingProjects = [],
  existingTypes = [],
  aluminumSeries = []
}) => {
  const [items, setItems] = useState([]);
  const [projectName, setProjectName] = useState("");
  const [internalProjectName, setInternalProjectName] = useState("");
  const [dbTypes, setDbTypes] = useState([]);
  
  // State for combobox open states
  const [openSeriesIndex, setOpenSeriesIndex] = useState(null);
  const [openDescIndex, setOpenDescIndex] = useState(null);
  const [seriesSearch, setSeriesSearch] = useState("");
  const [descSearch, setDescSearch] = useState("");
  
  // Bulk actions state
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [bulkSeriesOpen, setBulkSeriesOpen] = useState(false);
  const [bulkDescOpen, setBulkDescOpen] = useState(false);
  const [bulkColorOpen, setBulkColorOpen] = useState(false);
  const [bulkSeriesSearch, setBulkSeriesSearch] = useState("");
  const [bulkDescSearch, setBulkDescSearch] = useState("");
  const [bulkColorSearch, setBulkColorSearch] = useState("");
  
  // Colors state
  const [availableColors, setAvailableColors] = useState([]);
  const [openColorIndex, setOpenColorIndex] = useState(null);
  const [colorSearch, setColorSearch] = useState("");
  const [newColorInput, setNewColorInput] = useState("");
  
  // Installation and PDR state for this project
  const [projectInstallation, setProjectInstallation] = useState({ 
    cosM2i: '123', 
    emoXM2: '0.29', 
    selloXM2: '2.62', 
    destaXM2: '1.99', 
    eppXM2: '0.57', 
    utiPercent: '11',
    adicional: '0'
  });
  const [projectPdr, setProjectPdr] = useState({ quantity: '', unitValue: '' });

  // Fetch existing types and colors from DB
  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const [typesRes, materialsRes] = await Promise.all([
          api.get(`${API}/quote-types`),
          api.get(`${API}/materials`)
        ]);
        setDbTypes(typesRes.data || []);
        setAvailableColors(materialsRes.data?.colors || []);
      } catch (error) {
        console.error("Error fetching WindoorCraft data:", error?.message || error);
      }
    };
    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (parsedData) {
      setItems(parsedData.items || []);
      setProjectName(parsedData.project_name || "");
      setInternalProjectName("");
      
      // Load installation data if present
      if (parsedData.installation) {
        setProjectInstallation({
          cosM2i: parsedData.installation.cosM2i || '123',
          emoXM2: parsedData.installation.emoXM2 || '0.29',
          selloXM2: parsedData.installation.selloXM2 || '2.62',
          destaXM2: parsedData.installation.destaXM2 || '1.99',
          eppXM2: parsedData.installation.eppXM2 || '0.57',
          utiPercent: parsedData.installation.utiPercent || '11',
          adicional: parsedData.installation.adicional || '0'
        });
      }
      
      // Load PDR data if present
      if (parsedData.pdr) {
        setProjectPdr({
          quantity: parsedData.pdr.quantity || '',
          unitValue: parsedData.pdr.unitValue || ''
        });
      }
    }
  }, [parsedData]);

  const handleItemChange = (index, field, value) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    
    // If selecting a type from dropdown, update cost_per_m2 automatically
    if (field === 'matched_type_id' && value) {
      const selectedType = dbTypes.find(t => t.id === value);
      if (selectedType) {
        updated[index].matched_type_name = selectedType.name;
        updated[index].db_cost_per_m2 = selectedType.average_cost_per_m2 || 0;
        updated[index].match_confidence = 'manual';
        // Recalculate value
        const area = parseFloat(updated[index].area_m2) || 0;
        const qty = parseInt(updated[index].quantity) || 1;
        updated[index].calculated_value = updated[index].db_cost_per_m2 * area * qty;
      }
    }
    
    // Recalculate area if width or height changes
    if (field === 'width_m' || field === 'height_m') {
      const width = parseFloat(updated[index].width_m) || 0;
      const height = parseFloat(updated[index].height_m) || 0;
      updated[index].area_m2 = width * height;
    }
    
    // Recalculate value if cost_per_m2, area, or quantity changes
    if (field === 'db_cost_per_m2' || field === 'area_m2' || field === 'width_m' || field === 'height_m' || field === 'quantity') {
      const item = updated[index];
      const costPerM2 = parseFloat(item.db_cost_per_m2) || 0;
      const area = parseFloat(item.area_m2) || 0;
      const qty = parseInt(item.quantity) || 1;
      updated[index].calculated_value = costPerM2 * area * qty;
    }
    
    setItems(updated);
  };

  const handleDeleteItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const calculateTotal = () => {
    return items.reduce((sum, item) => sum + (item.calculated_value || 0), 0);
  };

  const handleSave = () => {
    onSave({
      items: items,
      project_name: internalProjectName || projectName,
      installation: {
        cosM2i: projectInstallation.cosM2i,
        emoXM2: projectInstallation.emoXM2,
        selloXM2: projectInstallation.selloXM2,
        destaXM2: projectInstallation.destaXM2,
        eppXM2: projectInstallation.eppXM2,
        utiPercent: projectInstallation.utiPercent,
        adicional: projectInstallation.adicional
      },
      pdr: {
        quantity: projectPdr.quantity,
        unitValue: projectPdr.unitValue
      }
    });
  };

  // Get unique projects from existing data
  const availableProjects = React.useMemo(() => {
    const projects = new Set();
    dbTypes.forEach(type => {
      if (type.project_name) projects.add(type.project_name);
    });
    existingProjects.forEach(p => projects.add(p));
    return Array.from(projects).sort();
  }, [dbTypes, existingProjects]);

  // Get available series for dropdown (from prop + detected in items)
  const availableSeries = React.useMemo(() => {
    const seriesSet = new Set(aluminumSeries || []);
    // Also add any series already in items
    items.forEach(item => {
      if (item.series) seriesSet.add(item.series);
    });
    // Add common series if not present
    ["SERIE 60", "SERIE 70", "SERIE 100", "75*150", "Aluminio", "uPVC"].forEach(s => seriesSet.add(s));
    return Array.from(seriesSet).sort();
  }, [aluminumSeries, items]);

  // Filter series based on search
  const filteredSeries = React.useMemo(() => {
    if (!seriesSearch) return availableSeries;
    return availableSeries.filter(s => 
      s.toLowerCase().includes(seriesSearch.toLowerCase())
    );
  }, [availableSeries, seriesSearch]);

  // Filter dbTypes based on search
  const filteredDbTypes = React.useMemo(() => {
    if (!descSearch) return dbTypes;
    return dbTypes.filter(t => 
      t.name?.toLowerCase().includes(descSearch.toLowerCase()) ||
      t.material?.toLowerCase().includes(descSearch.toLowerCase()) ||
      t.project_name?.toLowerCase().includes(descSearch.toLowerCase())
    );
  }, [dbTypes, descSearch]);

  // Filter series for bulk action
  const filteredBulkSeries = React.useMemo(() => {
    if (!bulkSeriesSearch) return availableSeries;
    return availableSeries.filter(s => 
      s.toLowerCase().includes(bulkSeriesSearch.toLowerCase())
    );
  }, [availableSeries, bulkSeriesSearch]);

  // Filter dbTypes for bulk action
  const filteredBulkDbTypes = React.useMemo(() => {
    if (!bulkDescSearch) return dbTypes;
    return dbTypes.filter(t => 
      t.name?.toLowerCase().includes(bulkDescSearch.toLowerCase()) ||
      t.material?.toLowerCase().includes(bulkDescSearch.toLowerCase())
    );
  }, [dbTypes, bulkDescSearch]);

  // Filter colors for bulk action
  const filteredBulkColors = React.useMemo(() => {
    if (!bulkColorSearch) return availableColors;
    return availableColors.filter(c => 
      c.toLowerCase().includes(bulkColorSearch.toLowerCase())
    );
  }, [availableColors, bulkColorSearch]);

  // Filter colors based on search
  const filteredColors = React.useMemo(() => {
    if (!colorSearch) return availableColors;
    return availableColors.filter(c => 
      c.toLowerCase().includes(colorSearch.toLowerCase())
    );
  }, [availableColors, colorSearch]);

  // Add new color to DB
  const handleAddNewColor = async () => {
    if (!newColorInput.trim()) return;
    
    try {
      const response = await api.post(`${API}/colors`, { 
        color_name: newColorInput.trim() 
      });
      setAvailableColors(prev => [...prev, response.data.color].sort());
      setNewColorInput("");
      toast.success(`Color "${response.data.color}" agregado`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al agregar color");
    }
  };

  // Toggle item selection
  const toggleItemSelection = (index) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedItems(newSelected);
  };

  // Select/deselect all items
  const toggleSelectAll = () => {
    if (selectedItems.size === items.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(items.map((_, i) => i)));
    }
  };

  // Apply series to selected items (or all if none selected)
  const applyBulkSeries = (series) => {
    const indicesToUpdate = selectedItems.size > 0 
      ? Array.from(selectedItems) 
      : items.map((_, i) => i);
    
    const updated = [...items];
    indicesToUpdate.forEach(index => {
      updated[index] = { ...updated[index], series };
    });
    setItems(updated);
    setBulkSeriesOpen(false);
    setBulkSeriesSearch("");
    toast.success(`Serie "${series}" aplicada a ${indicesToUpdate.length} elementos`);
  };

  // Apply description to selected items (or all if none selected)
  const applyBulkDescription = (type) => {
    const indicesToUpdate = selectedItems.size > 0 
      ? Array.from(selectedItems) 
      : items.map((_, i) => i);
    
    const updated = [...items];
    indicesToUpdate.forEach(index => {
      updated[index] = {
        ...updated[index],
        matched_type_id: type.id,
        matched_type_name: type.name,
        db_cost_per_m2: type.average_cost_per_m2 || 0,
        match_confidence: 'manual',
        calculated_value: (type.average_cost_per_m2 || 0) * (updated[index].area_m2 || 0) * (updated[index].quantity || 1)
      };
    });
    setItems(updated);
    setBulkDescOpen(false);
    setBulkDescSearch("");
    toast.success(`"${type.name}" aplicado a ${indicesToUpdate.length} elementos`);
  };

  // Apply color to selected items (or all if none selected)
  const applyBulkColor = (color) => {
    const indicesToUpdate = selectedItems.size > 0 
      ? Array.from(selectedItems) 
      : items.map((_, i) => i);
    
    const updated = [...items];
    indicesToUpdate.forEach(index => {
      updated[index] = { ...updated[index], color };
    });
    setItems(updated);
    setBulkColorOpen(false);
    setBulkColorSearch("");
    toast.success(`Color "${color}" aplicado a ${indicesToUpdate.length} elementos`);
  };

  if (!parsedData) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Elementos WindoorCraft - {parsedData.filename}</DialogTitle>
          <DialogDescription>
            {items.length} elementos encontrados. Revisa y edita antes de guardar.
          </DialogDescription>
        </DialogHeader>

        {/* Project Info Section */}
        <div className="grid grid-cols-3 gap-4 py-3 flex-shrink-0 border-b">
          <div>
            <Label className="text-xs text-slate-500">Código del Proyecto</Label>
            <Input
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="Código del proyecto..."
              className="mt-1"
              data-testid="windoorcraft-project-code"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Proyecto Interno (Match BD)</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className="w-full mt-1 justify-between font-normal"
                  data-testid="internal-project-select"
                >
                  <span className="truncate">
                    {internalProjectName || "Seleccionar o escribir..."}
                  </span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[280px] p-0" align="start">
                <Command>
                  <CommandInput 
                    placeholder="Buscar o escribir proyecto..." 
                    value={internalProjectName}
                    onValueChange={setInternalProjectName}
                  />
                  <CommandList>
                    <CommandEmpty>
                      <div className="p-2 text-center text-sm">
                        {internalProjectName ? (
                          <span className="text-green-600">
                            Usar: "{internalProjectName}"
                          </span>
                        ) : (
                          "Escribe un nombre de proyecto"
                        )}
                      </div>
                    </CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value="__none__"
                        onSelect={() => setInternalProjectName("")}
                      >
                        <Check className={`mr-2 h-4 w-4 ${!internalProjectName ? "opacity-100" : "opacity-0"}`} />
                        -- Ninguno --
                      </CommandItem>
                      {availableProjects.map(proj => (
                        <CommandItem
                          key={proj}
                          value={proj}
                          onSelect={() => setInternalProjectName(proj)}
                        >
                          <Check className={`mr-2 h-4 w-4 ${internalProjectName === proj ? "opacity-100" : "opacity-0"}`} />
                          {proj}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <p className="text-xs text-slate-400 mt-1">
              Vincular con proyecto existente o crear nuevo
            </p>
          </div>
          <div className="text-right">
            <Label className="text-xs text-slate-500">Total Calculado</Label>
            <p className="text-2xl font-bold text-blue-600 mt-1">
              {formatCurrency(calculateTotal())}
            </p>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        <div className="flex items-center gap-3 py-2 px-3 bg-slate-50 rounded-lg border flex-shrink-0">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={selectedItems.size === items.length && items.length > 0}
              onChange={toggleSelectAll}
              className="h-4 w-4 rounded border-slate-300"
              data-testid="select-all-checkbox"
            />
            <span className="text-sm text-slate-600">
              {selectedItems.size > 0 ? `${selectedItems.size} seleccionados` : "Seleccionar"}
            </span>
          </div>
          
          <div className="h-4 w-px bg-slate-300" />
          
          {/* Bulk Series */}
          <Popover open={bulkSeriesOpen} onOpenChange={setBulkSeriesOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-8">
                <Package className="w-4 h-4 mr-1" />
                Aplicar Serie {selectedItems.size > 0 ? `(${selectedItems.size})` : "a Todos"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-0" align="start">
              <Command>
                <CommandInput 
                  placeholder="Buscar serie..." 
                  value={bulkSeriesSearch}
                  onValueChange={setBulkSeriesSearch}
                />
                <CommandList>
                  <CommandEmpty>No encontrada</CommandEmpty>
                  <CommandGroup>
                    {filteredBulkSeries.map((series) => (
                      <CommandItem
                        key={series}
                        value={series}
                        onSelect={() => applyBulkSeries(series)}
                      >
                        {series}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {/* Bulk Description */}
          <Popover open={bulkDescOpen} onOpenChange={setBulkDescOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-8">
                <FileSpreadsheet className="w-4 h-4 mr-1" />
                Aplicar Descripción {selectedItems.size > 0 ? `(${selectedItems.size})` : "a Todos"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-0" align="start">
              <Command>
                <CommandInput 
                  placeholder="Buscar descripción..." 
                  value={bulkDescSearch}
                  onValueChange={setBulkDescSearch}
                />
                <CommandList>
                  <CommandEmpty>No encontrada</CommandEmpty>
                  <CommandGroup>
                    {filteredBulkDbTypes.map((type) => (
                      <CommandItem
                        key={type.id}
                        value={type.name}
                        onSelect={() => applyBulkDescription(type)}
                      >
                        <div className="flex flex-col">
                          <span className="truncate">{type.name}</span>
                          <span className="text-xs text-slate-400">
                            ${type.average_cost_per_m2?.toFixed(2)}/m²
                          </span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {/* Bulk Color */}
          <Popover open={bulkColorOpen} onOpenChange={setBulkColorOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-8">
                <div className="w-4 h-4 mr-1 rounded-full bg-gradient-to-r from-red-400 via-yellow-400 to-blue-400" />
                Aplicar Color {selectedItems.size > 0 ? `(${selectedItems.size})` : "a Todos"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-0" align="start">
              <Command>
                <CommandInput 
                  placeholder="Buscar color..." 
                  value={bulkColorSearch}
                  onValueChange={setBulkColorSearch}
                />
                <CommandList>
                  <CommandEmpty>No encontrado</CommandEmpty>
                  <CommandGroup>
                    {filteredBulkColors.map((color) => (
                      <CommandItem
                        key={color}
                        value={color}
                        onSelect={() => applyBulkColor(color)}
                      >
                        {color}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {selectedItems.size > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 text-slate-500"
              onClick={() => setSelectedItems(new Set())}
            >
              Limpiar selección
            </Button>
          )}
        </div>

        {/* Scrollable Table Container */}
        <div className="flex-1 min-h-0 overflow-auto border rounded-lg mt-3">
          <Table>
            <TableHeader className="sticky top-0 bg-slate-50 z-10">
              <TableRow>
                <TableHead className="w-10">
                  <input
                    type="checkbox"
                    checked={selectedItems.size === items.length && items.length > 0}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 rounded border-slate-300"
                  />
                </TableHead>
                <TableHead className="w-14">Img</TableHead>
                <TableHead className="w-24">Código</TableHead>
                <TableHead className="w-24">Serie</TableHead>
                <TableHead className="w-28">Dimensiones</TableHead>
                <TableHead className="w-14 text-center">Cant.</TableHead>
                <TableHead className="w-24">Color</TableHead>
                <TableHead className="w-40">Descripción BD</TableHead>
                <TableHead className="w-32">Observaciones</TableHead>
                <TableHead className="w-20 text-right">$/m²</TableHead>
                <TableHead className="w-24 text-right">Valor</TableHead>
                <TableHead className="w-8"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item, index) => (
                <TableRow 
                  key={item.id || index}
                  className={selectedItems.has(index) ? "bg-blue-50" : ""}
                >
                  <TableCell className="p-2">
                    <input
                      type="checkbox"
                      checked={selectedItems.has(index)}
                      onChange={() => toggleItemSelection(index)}
                      className="h-4 w-4 rounded border-slate-300"
                      data-testid={`item-checkbox-${index}`}
                    />
                  </TableCell>
                  <TableCell className="p-2">
                    {item.image_data ? (
                      <ImageThumbnail
                        src={item.image_data}
                        alt={item.item_code}
                        className="w-10 h-10 object-contain rounded"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center">
                        <FileSpreadsheet className="w-5 h-5 text-slate-400" />
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="p-2">
                    <Input
                      value={item.custom_name || item.item_code}
                      onChange={(e) => handleItemChange(index, 'custom_name', e.target.value)}
                      className="h-8 text-sm"
                      data-testid={`item-code-${index}`}
                    />
                  </TableCell>
                  <TableCell className="p-2">
                    {/* Combobox for Series selection */}
                    <Popover 
                      open={openSeriesIndex === index} 
                      onOpenChange={(open) => {
                        setOpenSeriesIndex(open ? index : null);
                        if (!open) setSeriesSearch("");
                      }}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          className="h-8 w-32 justify-between text-sm font-normal"
                          data-testid={`item-series-${index}`}
                        >
                          <span className="truncate">
                            {item.series || "Serie..."}
                          </span>
                          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-48 p-0" align="start">
                        <Command>
                          <CommandInput 
                            placeholder="Buscar serie..." 
                            value={seriesSearch}
                            onValueChange={setSeriesSearch}
                          />
                          <CommandList>
                            <CommandEmpty>No encontrada</CommandEmpty>
                            <CommandGroup>
                              {filteredSeries.map((series) => (
                                <CommandItem
                                  key={series}
                                  value={series}
                                  onSelect={() => {
                                    handleItemChange(index, 'series', series);
                                    setOpenSeriesIndex(null);
                                    setSeriesSearch("");
                                  }}
                                >
                                  <Check
                                    className={`mr-2 h-4 w-4 ${
                                      item.series === series ? "opacity-100" : "opacity-0"
                                    }`}
                                  />
                                  {series}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </TableCell>
                  <TableCell className="p-2">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          step="0.001"
                          value={item.width_m || ''}
                          onChange={(e) => {
                            const newWidth = parseFloat(e.target.value) || 0;
                            handleItemChange(index, 'width_m', newWidth);
                            // Recalculate area
                            const newArea = newWidth * (item.height_m || 0);
                            handleItemChange(index, 'area_m2', newArea);
                          }}
                          className="h-7 w-16 text-xs text-center"
                          placeholder="Ancho"
                          data-testid={`item-width-${index}`}
                        />
                        <span className="text-xs text-slate-400">×</span>
                        <Input
                          type="number"
                          step="0.001"
                          value={item.height_m || ''}
                          onChange={(e) => {
                            const newHeight = parseFloat(e.target.value) || 0;
                            handleItemChange(index, 'height_m', newHeight);
                            // Recalculate area
                            const newArea = (item.width_m || 0) * newHeight;
                            handleItemChange(index, 'area_m2', newArea);
                          }}
                          className="h-7 w-16 text-xs text-center"
                          placeholder="Alto"
                          data-testid={`item-height-${index}`}
                        />
                      </div>
                      <span className="text-xs text-slate-500 text-center">
                        = {formatNumber(item.area_m2, 4)} m²
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="p-2 text-center">
                    <Input
                      type="number"
                      min="1"
                      value={item.quantity || 1}
                      onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 1)}
                      className="h-8 w-14 text-center text-sm"
                      data-testid={`item-qty-${index}`}
                    />
                  </TableCell>
                  <TableCell className="p-2">
                    {/* Combobox for Color selection */}
                    <Popover 
                      open={openColorIndex === index} 
                      onOpenChange={(open) => {
                        setOpenColorIndex(open ? index : null);
                        if (!open) {
                          setColorSearch("");
                          setNewColorInput("");
                        }
                      }}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          className="h-8 w-24 justify-between text-xs font-normal"
                          data-testid={`item-color-${index}`}
                        >
                          <span className="truncate">
                            {item.color || "Color..."}
                          </span>
                          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-56 p-0" align="start">
                        <Command>
                          <CommandInput 
                            placeholder="Buscar color..." 
                            value={colorSearch}
                            onValueChange={setColorSearch}
                          />
                          <CommandList>
                            <CommandEmpty>
                              <div className="p-2 text-center">
                                <p className="text-xs text-slate-500 mb-2">No encontrado</p>
                                <div className="flex gap-1">
                                  <Input
                                    value={newColorInput}
                                    onChange={(e) => setNewColorInput(e.target.value)}
                                    placeholder="Nuevo color..."
                                    className="h-7 text-xs"
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddNewColor();
                                      }
                                    }}
                                  />
                                  <Button 
                                    size="sm" 
                                    className="h-7 px-2"
                                    onClick={handleAddNewColor}
                                  >
                                    <Plus className="h-3 w-3" />
                                  </Button>
                                </div>
                              </div>
                            </CommandEmpty>
                            <CommandGroup>
                              <CommandItem
                                value="__none__"
                                onSelect={() => {
                                  handleItemChange(index, 'color', null);
                                  setOpenColorIndex(null);
                                  setColorSearch("");
                                }}
                              >
                                <Check className={`mr-2 h-4 w-4 ${!item.color ? "opacity-100" : "opacity-0"}`} />
                                -- Sin color --
                              </CommandItem>
                              {filteredColors.map((color) => (
                                <CommandItem
                                  key={color}
                                  value={color}
                                  onSelect={() => {
                                    handleItemChange(index, 'color', color);
                                    setOpenColorIndex(null);
                                    setColorSearch("");
                                  }}
                                >
                                  <Check className={`mr-2 h-4 w-4 ${item.color === color ? "opacity-100" : "opacity-0"}`} />
                                  {color}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </TableCell>
                  <TableCell className="p-2">
                    {/* Combobox for Description BD selection */}
                    <Popover 
                      open={openDescIndex === index} 
                      onOpenChange={(open) => {
                        setOpenDescIndex(open ? index : null);
                        if (!open) setDescSearch("");
                      }}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          className="h-8 w-44 justify-between text-sm font-normal"
                          data-testid={`item-match-${index}`}
                        >
                          <span className="truncate text-left">
                            {item.matched_type_name || "-- Sin match --"}
                          </span>
                          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-72 p-0" align="start">
                        <Command>
                          <CommandInput 
                            placeholder="Buscar descripción..." 
                            value={descSearch}
                            onValueChange={setDescSearch}
                          />
                          <CommandList>
                            <CommandEmpty>No encontrada</CommandEmpty>
                            <CommandGroup>
                              <CommandItem
                                value="__none__"
                                onSelect={() => {
                                  handleItemChange(index, 'matched_type_id', null);
                                  const updated = [...items];
                                  updated[index].matched_type_name = null;
                                  updated[index].db_cost_per_m2 = 0;
                                  updated[index].match_confidence = null;
                                  updated[index].calculated_value = 0;
                                  setItems(updated);
                                  setOpenDescIndex(null);
                                  setDescSearch("");
                                }}
                              >
                                <Check
                                  className={`mr-2 h-4 w-4 ${
                                    !item.matched_type_id ? "opacity-100" : "opacity-0"
                                  }`}
                                />
                                -- Sin match --
                              </CommandItem>
                              {filteredDbTypes.map((type) => (
                                <CommandItem
                                  key={type.id}
                                  value={type.name}
                                  onSelect={() => {
                                    handleItemChange(index, 'matched_type_id', type.id);
                                    setOpenDescIndex(null);
                                    setDescSearch("");
                                  }}
                                >
                                  <Check
                                    className={`mr-2 h-4 w-4 ${
                                      item.matched_type_id === type.id ? "opacity-100" : "opacity-0"
                                    }`}
                                  />
                                  <div className="flex flex-col">
                                    <span className="truncate">{type.name}</span>
                                    <span className="text-xs text-slate-400">
                                      ${type.average_cost_per_m2?.toFixed(2)}/m²
                                    </span>
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    {item.match_confidence && (
                      <Badge 
                        variant={item.match_confidence === 'exact' || item.match_confidence === 'high' ? 'default' : item.match_confidence === 'manual' ? 'secondary' : 'outline'}
                        className="mt-1 text-xs"
                      >
                        {item.match_confidence === 'manual' ? 'manual' : item.match_confidence}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="p-2">
                    <Input
                      value={item.observations || ''}
                      onChange={(e) => handleItemChange(index, 'observations', e.target.value)}
                      className="h-8 w-full text-xs"
                      placeholder="Observaciones..."
                      data-testid={`item-obs-${index}`}
                    />
                  </TableCell>
                  <TableCell className="p-2 text-right">
                    <Input
                      type="number"
                      step="0.01"
                      value={item.db_cost_per_m2 || ''}
                      onChange={(e) => handleItemChange(index, 'db_cost_per_m2', parseFloat(e.target.value) || 0)}
                      className="h-8 w-20 text-right text-sm"
                      placeholder="$/m²"
                      data-testid={`item-cost-${index}`}
                    />
                  </TableCell>
                  <TableCell className="p-2 text-right font-medium text-sm">
                    {formatCurrency(item.calculated_value || 0)}
                  </TableCell>
                  <TableCell className="p-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteItem(index)}
                      className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                      data-testid={`item-delete-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Installation, Totals, and PDR Section for this project */}
        <div className="grid grid-cols-3 gap-4 mt-4 p-4 bg-slate-50 rounded-lg border flex-shrink-0">
          {/* Installation */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm flex items-center gap-2 text-green-700">
              <Package className="w-4 h-4" />
              Instalación del Proyecto (Soles)
            </h4>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">COS M2i (S/.)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={projectInstallation.cosM2i || '123'}
                  onChange={(e) => setProjectInstallation({
                    ...projectInstallation,
                    cosM2i: e.target.value
                  })}
                  className="h-7 text-xs"
                  placeholder="123"
                  data-testid="project-cos-m2i"
                />
              </div>
              <div>
                <Label className="text-xs">EMO X M2</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={projectInstallation.emoXM2 || '0.29'}
                  onChange={(e) => setProjectInstallation({
                    ...projectInstallation,
                    emoXM2: e.target.value
                  })}
                  className="h-7 text-xs"
                />
              </div>
              <div>
                <Label className="text-xs">SELLO X M2</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={projectInstallation.selloXM2 || '2.62'}
                  onChange={(e) => setProjectInstallation({
                    ...projectInstallation,
                    selloXM2: e.target.value
                  })}
                  className="h-7 text-xs"
                />
              </div>
              <div>
                <Label className="text-xs">DESTA X M2</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={projectInstallation.destaXM2 || '1.99'}
                  onChange={(e) => setProjectInstallation({
                    ...projectInstallation,
                    destaXM2: e.target.value
                  })}
                  className="h-7 text-xs"
                />
              </div>
              <div>
                <Label className="text-xs">EPP X M2</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={projectInstallation.eppXM2 || '0.57'}
                  onChange={(e) => setProjectInstallation({
                    ...projectInstallation,
                    eppXM2: e.target.value
                  })}
                  className="h-7 text-xs"
                />
              </div>
              <div>
                <Label className="text-xs">% UTI</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  value={projectInstallation.utiPercent || '11'}
                  onChange={(e) => setProjectInstallation({
                    ...projectInstallation,
                    utiPercent: e.target.value
                  })}
                  className="h-7 text-xs"
                  data-testid="project-uti-percent"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <Label className="text-xs text-orange-600 font-medium">ADICIONAL (ACC SOFT)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={projectInstallation.adicional || '0'}
                  onChange={(e) => setProjectInstallation({
                    ...projectInstallation,
                    adicional: e.target.value
                  })}
                  className="h-7 text-xs border-orange-300"
                  placeholder="5000.00"
                  data-testid="project-adicional"
                />
              </div>
            </div>
            {(() => {
              const cosM2i = parseFloat(projectInstallation.cosM2i) || 123;
              const emoXM2 = parseFloat(projectInstallation.emoXM2) || 0.29;
              const selloXM2 = parseFloat(projectInstallation.selloXM2) || 2.62;
              const destaXM2 = parseFloat(projectInstallation.destaXM2) || 1.99;
              const eppXM2 = parseFloat(projectInstallation.eppXM2) || 0.57;
              const utiPercent = parseFloat(projectInstallation.utiPercent) || 11;
              const adicional = parseFloat(projectInstallation.adicional) || 0;
              
              const baseCostM2 = cosM2i + emoXM2 + selloXM2 + destaXM2 + eppXM2;
              const totalArea = items.reduce((sum, item) => sum + ((parseFloat(item.area_m2) || 0) * (parseInt(item.quantity) || 1)), 0);
              const installBase = baseCostM2 * (1 + utiPercent / 100) * totalArea;
              const installTotal = installBase + adicional;
              
              return (
                <div className="text-xs space-y-1 bg-green-50 p-2 rounded border border-green-200">
                  <div className="flex justify-between">
                    <span>COS FIN M2:</span>
                    <span className="font-mono">S/ {baseCostM2.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total (sin adic.):</span>
                    <span className="font-mono">S/ {installBase.toFixed(2)}</span>
                  </div>
                  {adicional > 0 && (
                    <div className="flex justify-between text-orange-600">
                      <span>+ Adicional:</span>
                      <span className="font-mono">S/ {adicional.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold text-green-700 border-t pt-1">
                    <span>TOTAL INSTALACIÓN:</span>
                    <span className="font-mono">S/ {installTotal.toFixed(2)}</span>
                  </div>
                </div>
              );
            })()}
          </div>
          
          {/* Total M² del Proyecto */}
          <div className="space-y-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-medium text-sm flex items-center gap-2 text-blue-700">
              <Calculator className="w-4 h-4" />
              Resumen del Proyecto
            </h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-blue-600">Total m²:</span>
                <span className="text-base font-bold text-blue-800">
                  {items.reduce((sum, item) => sum + ((item.area_m2 || 0) * (item.quantity || 1)), 0).toFixed(2)} m²
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-blue-600">Total Suministro:</span>
                <span className="text-sm font-semibold text-blue-700">
                  S/. {items.reduce((sum, item) => sum + (item.calculated_value || 0), 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex items-center justify-between border-t pt-2 mt-2">
                <span className="text-xs font-medium text-blue-700">Elementos:</span>
                <span className="text-sm font-bold text-blue-800">{items.length}</span>
              </div>
            </div>
          </div>
          
          {/* PDR */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm flex items-center gap-2 text-orange-700">
              <FileText className="w-4 h-4" />
              PDR - Prevencionista de Riesgo (Soles)
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Cantidad</Label>
                <Input
                  type="number"
                  min="0"
                  value={projectPdr.quantity || ''}
                  onChange={(e) => setProjectPdr({
                    ...projectPdr,
                    quantity: e.target.value
                  })}
                  className="h-8"
                  placeholder="1"
                  data-testid="project-pdr-qty"
                />
              </div>
              <div>
                <Label className="text-xs">Valor Unitario (S/.)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={projectPdr.unitValue || ''}
                  onChange={(e) => setProjectPdr({
                    ...projectPdr,
                    unitValue: e.target.value
                  })}
                  className="h-8"
                  placeholder="100.00"
                  data-testid="project-pdr-value"
                />
              </div>
            </div>
            {projectPdr.quantity && projectPdr.unitValue && (
              <div className="text-sm text-orange-600">
                Total PDR: S/. {(
                  (parseInt(projectPdr.quantity) || 0) * (parseFloat(projectPdr.unitValue) || 0)
                ).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="mt-4 flex-shrink-0">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={isSaving || items.length === 0}>
            {isSaving ? "Guardando..." : `Guardar ${items.length} elementos`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
/**
 * Factory Export Button
 * Exports factory items to Excel in PARA SOFTWARE format
 */
export const FactoryExportButton = ({ 
  factoryTypes, 
  utiPercent = 0, 
  projectName = "EXPORT",
  onValidationResult 
}) => {
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    if (!factoryTypes || factoryTypes.length === 0) {
      toast.error("No hay elementos para exportar");
      return;
    }

    setIsExporting(true);

    try {
      // Prepare items for export
      const items = factoryTypes.map(type => ({
        item_code: type.item_code || type.name,
        width_m: type.entries?.[0]?.width_m || 0,
        height_m: type.entries?.[0]?.height_m || 0,
        quantity: type.entries_count || 1,
        area_m2: type.total_area / (type.entries_count || 1),
        cost_per_m2: type.average_cost_per_m2 || 0
      }));

      // Calculate total for validation
      const totalSupply = items.reduce((sum, item) => {
        return sum + (item.area_m2 * item.cost_per_m2 * item.quantity);
      }, 0);

      // Validate UTI
      const validationResponse = await api.post(`${API}/factory/validate-uti`, {
        supply_total: totalSupply,
        uti_percent: utiPercent
      });

      if (onValidationResult) {
        onValidationResult(validationResponse.data);
      }

      if (!validationResponse.data.is_valid) {
        toast.warning(validationResponse.data.message);
      }

      // Export to Excel
      const response = await api.post(
        `${API}/factory/export-excel`,
        {
          items: items,
          uti_percent: utiPercent,
          project_name: projectName
        },
        {
          responseType: 'blob'
        }
      );

      // Download file
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `COTI_${projectName}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Excel exportado correctamente");

    } catch (error) {
      // console.error("Error exporting:", error);
      toast.error(error.response?.data?.detail || "Error al exportar Excel");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Button 
      onClick={handleExport} 
      disabled={isExporting || !factoryTypes?.length}
      variant="outline"
      className="gap-2"
      data-testid="factory-export-excel-btn"
    >
      <Download className="w-4 h-4" />
      {isExporting ? "Exportando..." : "COTI en Excel"}
    </Button>
  );
};

export default {
  WindoorCraftUploadSection,
  WindoorCraftParsedItemsModal,
  FactoryExportButton
};
